import { useEffect, useMemo, useState } from "react"
import { MdAdsClick, MdWarningAmber } from "react-icons/md"
import Modal from "../../components/Modal"
import Button from "../../components/Form/Button"
import Checkbox from "../../components/Form/Checkbox"
import Input from "../../components/Form/Input"
import Loading from "../layouts/Loading"
import api from "../../services/api"
import styles from "./metaIntegration.module.css"

const PAGE_SIZE = 100
const normalizePixelId = value => String(value || "")

const ModalMetaPixelSelection = ({ connection, closeModal, onSaved }) => {
    const [pixels, setPixels] = useState([])
    const [selected, setSelected] = useState(new Set())
    const [search, setSearch] = useState("")
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [loaded, setLoaded] = useState(false)
    const [error, setError] = useState("")

    useEffect(() => {
        api.post("/integrations/meta/pixels/discover", { meta_user_account_id: connection._id })
            .then(response => {
                const data = response?.data?.data || {}
                setLoaded(true)
                setPixels((data.pixels || []).map(account => ({ ...account, id: normalizePixelId(account.id) })).filter(account => account.id))
                setSelected(new Set((data.selected_ids || []).map(normalizePixelId).filter(Boolean)))
            })
            .catch(err => setError(err?.response?.data?.message || "Não foi possível consultar os pixels na Meta."))
            .finally(() => setLoading(false))
    }, [connection._id])

    const filtered = useMemo(() => {
        const term = search.trim().toLocaleLowerCase("pt-BR")
        return pixels.filter(account => !term || `${account.name || ""} ${account.id || ""} ${account.business_name || ""}`.toLocaleLowerCase("pt-BR").includes(term))
    }, [pixels, search])

    const toggle = (setter, id) => setter(previous => {
        const next = new Set(previous)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
    })

    const authorizeFiltered = () => {
        const ids = filtered.map(account => normalizePixelId(account.id)).filter(Boolean)
        setSelected(previous => new Set([...previous, ...ids]))
    }

    const clearFiltered = () => {
        const ids = new Set(filtered.map(account => normalizePixelId(account.id)))
        setSelected(previous => new Set([...previous].filter(id => !ids.has(id))))
    }

    const save = async () => {
        setSaving(true)
        setError("")
        try {
            await api.patch("/integrations/meta/pixels/selection", {
                meta_user_account_id: connection._id,
                pixel_ids: [...selected],
            })
            await onSaved?.()
            closeModal()
        } catch (err) {
            setError(err?.response?.data?.message || "Não foi possível salvar os pixels autorizados.")
        } finally { setSaving(false) }
    }

    return <Modal icon={<MdAdsClick />} title="Pixels autorizados" description={`Selecione os pixels de ${connection.name || "esta conexão"} para usar nesta organização.`} closeModal={saving ? () => {} : closeModal} closeOnEsc={!saving} bodyClassName={styles.assetSelectionBody}>
        {loading ? <div className={styles.selectionLoading}><Loading /><p>Consultando os pixels diretamente na Meta…</p></div> : <>
            <div className={styles.selectionNotice}><MdWarningAmber /><span>Selecione os pixels que poderão receber conversões desta organização, mesmo sem vínculo com uma conta de anúncios.</span></div>
            <div className={styles.selectionToolbar}>
                <Input type="search" value={search} onChange={event => { setSearch(event.target.value); setVisibleCount(PAGE_SIZE) }} placeholder="Pesquisar por nome, ID ou portfólio" aria-label="Pesquisar pixels Meta Ads" />
                <span><strong>{selected.size}</strong> de {pixels.length} autorizados</span>
            </div>
            <div className={styles.selectionQuickActions}><Button type="button" variant="secondary" size="sm" fullWidth={false} onClick={authorizeFiltered}>Autorizar resultados</Button><Button type="button" variant="secondary" size="sm" fullWidth={false} onClick={clearFiltered}>Limpar resultados</Button></div>
            {error && <div className={styles.selectionError}>{error}</div>}
            <div className={styles.selectionList}>
                {filtered.slice(0, visibleCount).map(account => {
                    const id = normalizePixelId(account.id)
                    return <div className={styles.selectionRow} key={id}>
                        <span className={styles.selectionIdentity}><MdAdsClick /><span><strong>{account.name || "Pixel"}</strong><small>{id}{account.currency ? ` · ${account.currency}` : ""}{account.business_name ? ` · ${account.business_name}` : ""}</small></span></span>
                        <div className={styles.selectionPermissions}>
                            <button type="button" aria-pressed={selected.has(id)} onClick={() => toggle(setSelected, id)}><Checkbox checked={selected.has(id)} /><span><strong>Autorizar pixel</strong><small>Conversões offline</small></span></button>
                        </div>
                    </div>
                })}
                {!filtered.length && <p className={styles.emptySelection}>Nenhum pixel encontrado.</p>}
                {visibleCount < filtered.length && <Button type="button" variant="secondary" fullWidth={false} onClick={() => setVisibleCount(value => value + PAGE_SIZE)}>Carregar mais</Button>}
            </div>
            <div className={styles.selectionFooter}><Button type="button" variant="secondary" fullWidth={false} onClick={closeModal}>Cancelar</Button><Button type="button" fullWidth={false} onClick={save} disabled={!loaded || saving}>Salvar {selected.size} pixel(s)</Button></div>
        </>}
        {saving && <Loading fullPage={true} />}
    </Modal>
}

export default ModalMetaPixelSelection
